<template lang="pug">
	.audio-tracker-equalizer.flex-col.full-width.space-large.center.align-center
		.flex-row.full-width.space
			.flex-col.space.align-center
				.font-tiny.upper.grey {{$tCaption('Pre')}}
				AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="228px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPreAnalyser() }")
			.flex-1
				DynamicEqChart(:series="chartSeries", :width="560", :height="300", @changing="onChartChange")
			.flex-col.space.align-center
				.font-tiny.upper.grey {{$tCaption('Post')}}
				AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="228px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPostAnalyser() }")
		table
			tr
				td {{$tCaption('Type')}}
				td {{$tCaption('Q')}}
				td {{$tCaption('Frequency')}}
				td {{$tCaption('Gain')}}
				td
			tr.filter-value(v-for="(filter, idx) in orderedFilters")
				td
					.flex-row.space.align-center
						i(:class="[types[filter.type] && types[filter.type].icon]")
						DxSelectBox(:dataSource="types", :value.sync="filter.type", valueExpr="id", displayExpr="text", width="80px", :dropDownOptions="{ minWidth: '120px' }", :width="100")
							template(v-slot:item="{ data }")
								.flex-row.space.align-center
									i(:class="[data.icon]")
									span {{data.text}}
				td
					DxNumberBox(:value.sync="filter.q", width="40px")
				td
					DxNumberBox(:value.sync="filter.frequency", width="80px")
				td
					DxNumberBox(:value.sync="filter.gain", width="60px")
				td
					AudioTrackerButton(icon="fas fa-times", @click="() => values.splice(values.findIndex(v => v.id === filter.id), 1)")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import AudioTrackerButton from '../AudioTrackerButton.vue';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxNumberBox from 'devextreme-vue/number-box';
	import AudioTrackerVUMeter from '../AudioTrackerVUMeter.vue';
	import audioHelper from 'view/audioTracker/audio-helper';
	import DynamicEqChart from './DynamicEqChart.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackerEqualizer',
		name: 'audio-tracker-equalizer',
		components: { DynamicEqChart, DxSelectBox, DxNumberBox, AudioTrackerButton, AudioTrackerVUMeter },
		props: {
			playing: VueTypes.bool.def(false),
			tracker: VueTypes.any.def(null),
			instance: VueTypes.any.def(null)
		},
		data() {
			return {
				types: [
					{ id: 0, text: this.$tCaption('Peaking'), icon: 'ff-peaking-filter' },
					{ id: 1, text: this.$tCaption('Low-pass'), icon: 'ff-low-pass-filter'  },
					{ id: 2, text: this.$tCaption('High-pass'), icon: 'ff-high-pass-filter'  },
					{ id: 3, text: this.$tCaption('Band-pass'), icon: 'ff-band-pass-filter'  },
					{ id: 4, text: this.$tCaption('Low-shelf'), icon: 'ff-low-shelf-filter'  },
					{ id: 5, text: this.$tCaption('High-shelf'), icon: 'ff-high-shelf-filter'  },
					{ id: 6, text: this.$tCaption('Notch'), icon: 'ff-notch-filter'  },
					{ id: 7, text: this.$tCaption('All-pass'), icon: 'ff-all-pass-filter'  },
				]
			};
		},
		computed: {
			values: {
				get: function() {
					return this.instance?.values;
				},
				set: function(v) {
					this.instance.setValues(v)
				}
			},
			orderedFilters() {
				return _.orderBy(this.values, v => v?.frequency);
			},
			chartSeries() {
				return [
					{
						name: "eq",
						showPointLabels: true,
						pointLabelColor: "#fff",
						type: "spline-area",
						points: this.values.map(f => ({
							frequency: f.frequency,
							dB: f.gain,
							color: f.gain === 0 ? '#f2764966' : null,
							label: f.gain === 0 ? ' ' : null,
						})),
						lineColor: "#f27649",
						lineWidth: 2,
						fillColor: "#f27649",
						pointColor: "#f27649",
						editable: true
					}
				];
			}
		},
		watch: {
			values: {
				handler() {
					this.$emit('change', this.values);
					this.$applyValues();
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__)
				window.eqWapi = this;
			this.$applyValues = _.debounce(this.applyValues, 300, {
				maxWait: 1000,
				leading: false,
				trailing: true
			});
		},
		wapiInstance(opts) {
			if (__DEBUG__)
				console.debug('Instantiate equalizer');

			const values = [];
			let inputGainNode = null;
			let outputGainNode = null;
			let equalizerNodes = [];
			let preAnalyserNode = null;
			let postAnalyserNode = null;

			const filterTypes = ['peaking', 'lowpass', 'highpass', 'bandpass', 'lowshelf', 'highshelf', 'notch', 'allpass'];

			function limitBiquadFilterQ(filterNode) {
				//100Hz-10kHz
				const maxQValues = {
					"lowpass": 100,
					"highpass": 100,
					"bandpass": 100,
					"notch": 100,
					"peaking": 100,
					"lowshelf": 10,
					"highshelf": 10,
					"allpass": 100,
				};

				// < 100Hz || > 10Khz
				const maxQBorderValues = {
					"lowpass": 10,
					"highpass": 10,
					"bandpass": 30,
					"notch": 100,
					"peaking": 10,
					"lowshelf": 1,
					"highshelf": 1,
					"allpass": 10,
				};

				let baseMaxQ = maxQValues[filterNode.type] || 100;
				let borderMaxQ = maxQBorderValues[filterNode.type] || 10;
				let frequency = filterNode.frequency.value;
				let maxQ = baseMaxQ;

				if (frequency < 100 || frequency > 10000) {
					maxQ = borderMaxQ;
				}

				if (filterNode.Q.value > maxQ) {
					filterNode.Q.value = maxQ;
				}
			}

			const initDynamicEqualizer = (audioContext, trackEqValues) => {
				if (__DEBUG__)
					console.debug('Init eq vals', { trackEqValues });
				const createEqBandFilter = (opts, audioContext) => {
					var filter = audioContext.createBiquadFilter();

					filter.type = filterTypes[opts?.type || 0] || 'peaking';
					filter.frequency.value = opts?.frequency;
					filter.Q.value = opts?.q || 1;
					filter.gain.value = opts?.gain || 0;

					limitBiquadFilterQ(filter);

					return filter;
				}
				if (!trackEqValues?.length) return [];
				var filters = trackEqValues.map(v => createEqBandFilter(v, audioContext));

				filters.reduce((prev, curr) => {
					prev.connect(curr);
					return curr;
				});

				return filters;
			}

			let audioContext = null;

			const applyDynamicValues = () => {
				if (audioContext && equalizerNodes.length !== values.length) {
					equalizerNodes = initDynamicEqualizer(audioContext, values);

					inputGainNode.disconnect();
					preAnalyserNode.connect(inputGainNode);
					inputGainNode.connect(equalizerNodes[0]);
					equalizerNodes[equalizerNodes.length-1].connect(outputGainNode);
				}

				if (equalizerNodes.length === values.length) {
					values.map((f, i) => {
						if (equalizerNodes[i]) {
							equalizerNodes[i].frequency.value = f.frequency;
							equalizerNodes[i].gain.value = f.gain;
							equalizerNodes[i].type = filterTypes[f?.type || 0] || 'peaking';
							equalizerNodes[i].Q.value = f?.q || 1;

							limitBiquadFilterQ(equalizerNodes[i]);
						}
					})
				}
			}

			return {
				getPreAnalyser() {
					return preAnalyserNode;
				},
				getPostAnalyser() {
					return postAnalyserNode;
				},
				getEqualizerNodes: () => equalizerNodes,
				values,
				component: () => opts.component,
				getValues: () => values,
				setValues: (v) => {
					values.splice(0,100);

					if (v.length) {
						values.push(...v);
					} else {
						values.push(...[60, 250, 1000, 4000, 16000].map(f => ({
							frequency: f,
							gain: 0,
							type: 0,
							q: 1
						})));
					}

					applyDynamicValues();
				},
				getUI: () => ({
					width: 720,
					height: 700
				}),
				getAudioPipeline(opts) {
					audioContext = opts?.audioContext || new AudioContext();

					inputGainNode = audioContext.createGain();
					outputGainNode = audioContext.createGain();

					preAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					preAnalyserNode.fftSize = 512;

					postAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					postAnalyserNode.fftSize = 512;

					applyDynamicValues();

					outputGainNode.connect(postAnalyserNode);

					return {
						input: preAnalyserNode,
						output: postAnalyserNode
					}
				},
				setPlayState(isPlaying) {
				},
				applyDynamicValues
			}
		},
		methods: {
			onChartChange(opts) {
				this.values[opts.pointIndex].frequency = opts.frequency;
				this.values[opts.pointIndex].gain = opts.dB;
			},
			prepareValue(value) {
				return value.map(f => {
					if (!f.hasOwnProperty('type')) {
						f.type = 0;
					}

					if (!f.hasOwnProperty('q')) {
						f.q = 1;
					}

					return f;
				});
			},
			applyValues() {
				this.instance.applyDynamicValues();
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-equalizer {
		@import "main/themes/generated/variables.base.scss";

		.eq-holder {
			width: 300px;
			max-width: 300px;

			.eq-text {
				text-align: center;
			}
		}

		.eq-range-editor {
			height: 250px;
		}

	}
</style>
